import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';

import { useListFiles } from '../../hooks/useLeadImportApi';
import type { FileInfo } from '../../utils/types';

interface LatestJob {
  jobId: string;
  status: string;
  totalRows?: number;
  successfulRows?: number;
  failedRows?: number;
  completedAt?: string;
}

interface FileWithJob extends FileInfo {
  latestJob?: LatestJob;
}

interface PreviousFilesListProps {
  onSelectFile: (file: FileInfo) => void;
}

const PreviousFilesList: React.FC<PreviousFilesListProps> = ({
  onSelectFile,
}) => {
  const { data: rawData, isLoading, error } = useListFiles();

  const rawFiles = Array.isArray(rawData)
    ? rawData
    : Array.isArray((rawData as any)?.files)
    ? (rawData as any).files
    : [];

  // Normalize and include latestJob
  const files: FileWithJob[] = rawFiles
    .map((f: any) => ({
      fileID: f.fileID || f.file_id || f.id || f.fileId || '',
      filename:
        f.filename ||
        f.originalFilename ||
        f.original_filename ||
        f.file_name ||
        f.name ||
        'Unnamed file',
      contentType: f.contentType || f.content_type || '',
      status: f.status || '',
      createdAt: f.createdAt || f.created_at || '',
      rowCount: f.rowCount ?? f.row_count ?? undefined,
      latestJob: f.latestJob || undefined,
    }))
    .filter((f: FileWithJob) => f.fileID && f.status !== 'pending_upload');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Unable to load previously uploaded files.
      </Alert>
    );
  }

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Previously Uploaded Files
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <List
        dense
        aria-label="Previously uploaded files"
        data-testid="previous-files-list"
      >
        {files.map((file) => (
          <ListItemButton
            key={file.fileID}
            onClick={() => onSelectFile(file)}
            sx={{ borderRadius: 1, mb: 0.5, alignItems: 'flex-start' }}
          >
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ wordBreak: 'break-word' }}
                    title={file.filename}
                  >
                    {truncateFilename(file.filename, 50)}
                  </Typography>
                  {file.latestJob && (
                    <Chip
                      label={getJobStatusLabel(file.latestJob)}
                      size="small"
                      color={getJobStatusColor(file.latestJob.status)}
                      variant="outlined"
                    />
                  )}
                </Box>
              }
              secondary={formatFileSecondary(file)}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

function formatFileSecondary(file: FileWithJob): string {
  const parts: string[] = [];

  if (file.createdAt) {
    parts.push(new Date(file.createdAt).toLocaleDateString());
  }

  if (file.rowCount !== undefined) {
    parts.push(`${file.rowCount.toLocaleString()} rows`);
  }

  if (file.latestJob) {
    const job = file.latestJob;
    if (job.successfulRows !== undefined && job.failedRows !== undefined) {
      parts.push(`✓ ${job.successfulRows} / ✗ ${job.failedRows}`);
    }
  }

  return parts.join(' • ');
}

function getJobStatusLabel(job: LatestJob): string {
  switch (job.status) {
    case 'completed':
      return 'Imported';
    case 'completed_with_errors':
      return 'Partial';
    case 'failed':
      return 'Failed';
    case 'processing':
      return 'Processing';
    case 'queued':
      return 'Queued';
    default:
      return job.status;
  }
}

function getJobStatusColor(
  status: string
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'completed_with_errors':
      return 'warning';
    case 'failed':
      return 'error';
    case 'processing':
    case 'queued':
      return 'info';
    default:
      return 'default';
  }
}

function truncateFilename(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const base = name.slice(0, name.length - ext.length);
  const truncatedBase = base.slice(0, maxLen - ext.length - 3);
  return `${truncatedBase}...${ext}`;
}

export default PreviousFilesList;
